import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { SearchModal } from 'designsystem/components/2-molecules/SearchModal';
import { LinkFeature } from 'features/Modules/Link';
import { useEffect, useRef } from 'react';
import { CludoSearchInputFeature } from '../CludoSearchInput/CludoSearchInputFeature';

export interface SearchModalProps {
	show: boolean;
	onClose: () => void;
	searchHeader: string;
}

export const SearchModalFeature: React.FC<SearchModalProps> = ({ show, onClose, searchHeader }) => {
	const dictionary = useDictionaryContext();
	const siteContext = useSiteContext();
	const pageContext = usePageContext();

	const inputRef = useRef<HTMLInputElement>(null);

	const { route: privacyRoute, url: privacyUrl, title: privacyName } = siteContext?.settings?.globalLinks?.privacyLink?.[0] ?? {};
	const conformedUrl = privacyUrl ? privacyUrl : privacyRoute?.path;

	useEffect(() => {
		if (!window.CludoSearch || !show || pageContext?.template === 'SearchPage') return;
		window.CludoSearch.isSearchResultPage = false;
		if (show) inputRef.current.focus();
	}, [show, pageContext]);

	// This useEffect ensures that when a url param change occurs while the SearchModal is open, the close func is called.
	// Nextjs can't detect when cludo makes changes to the url params when it searches. So we have to use history.replaceState
	// which is the function cludo.js uses to change the search params.
	// At some point we should revisit CludoSearchInputFeature, the way it works is a bit funky, which is why we close the modal
	// here instead of there. Once we revisit that, we can revisit this too.
	useEffect(() => {
		const replaceState = history.replaceState;

		const wrapper = (oldReplaceState: typeof history.replaceState) => {
			return function (this: History, ...args) {
				const res = oldReplaceState.apply(this, args);
				onClose();
				return res;
			};
		};

		history.replaceState = wrapper(replaceState);
		return () => {
			history.replaceState = replaceState;
		};
	}, [onClose, show]);

	return (
		<SearchModal
			id="searchModal"
			show={show}
			onClose={onClose}
			searchInput={<CludoSearchInputFeature ref={inputRef} ariaLabel="search-label" id="cludo-search-content-form" />}
			searchHeader={searchHeader}
			text={dictionary.getSearch('Disclaimer')}
			textLink={<LinkFeature url={conformedUrl} title={privacyName} target="_blank" className="SearchModal_link" />}
			ariaLabel={dictionary.getSearch('SearchModal')}
			ariaLabelClose={dictionary.getAction('Close')}
		/>
	);
};
